import { useTranslation } from 'react-i18next';
import { Moon, Sun, Monitor, Globe, User, LogOut } from 'lucide-react';
import { useUIStore, useAuthStore } from '../lib/store';
import { cn } from '../lib/utils';

export default function Settings() {
  const { t, i18n } = useTranslation();
  const { theme, setTheme } = useUIStore();
  const { user, logout } = useAuthStore();

  const themes = [
    { value: 'light', label: t('settings.light'), icon: Sun },
    { value: 'dark', label: t('settings.dark'), icon: Moon },
    { value: 'system', label: t('settings.system'), icon: Monitor },
  ] as const;

  const languages = [
    { value: 'en', label: 'English', nativeLabel: 'English' },
    { value: 'ar', label: 'Arabic', nativeLabel: 'العربية' },
  ];

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  };

  return (
    <div className="notion-container animate-fade-in max-w-2xl">
      <h1 className="notion-heading-1 mb-8">{t('settings.title')}</h1>

      {/* Appearance Section */}
      <section className="mb-10">
        <h2 className="notion-heading-3 mb-4 text-muted-foreground">
          {t('settings.appearance')}
        </h2>

        {/* Theme */}
        <div className="mb-6">
          <label className="text-sm font-medium mb-3 block">
            {t('settings.theme')}
          </label>
          <div className="flex flex-wrap gap-2">
            {themes.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => setTheme(value)}
                className={cn(
                  'flex items-center gap-2 rounded-lg border px-4 py-2.5 transition-all',
                  theme === value
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border hover:border-primary/50 hover:bg-secondary'
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="text-sm">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Language */}
        <div>
          <label className="text-sm font-medium mb-3 block">
            {t('settings.language')}
          </label>
          <div className="flex flex-wrap gap-2">
            {languages.map(({ value, nativeLabel }) => (
              <button
                key={value}
                onClick={() => handleLanguageChange(value)}
                className={cn(
                  'flex items-center gap-2 rounded-lg border px-4 py-2.5 transition-all',
                  i18n.language === value
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-border hover:border-primary/50 hover:bg-secondary'
                )}
              >
                <Globe className="h-4 w-4" />
                <span className="text-sm">{nativeLabel}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Account Section */}
      <section className="mb-10">
        <h2 className="notion-heading-3 mb-4 text-muted-foreground">
          {t('settings.account')}
        </h2>

        {user ? (
          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-lg font-bold text-white">
                {user.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div>
                <div className="font-medium">{user.name}</div>
                <div className="text-sm text-muted-foreground">{user.email}</div>
              </div>
            </div>
            <button
              onClick={logout}
              className="btn-ghost text-destructive hover:bg-destructive/10 w-full justify-start"
            >
              <LogOut className="h-4 w-4" />
              {t('settings.logout')}
            </button>
          </div>
        ) : (
          <div className="rounded-lg border p-6 text-center">
            <User className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground mb-4">
              {t('errors.unauthorized')}
            </p>
            <button className="btn-primary">
              {i18n.language === 'ar' ? 'تسجيل الدخول' : 'Sign in'}
            </button>
          </div>
        )}
      </section>

      {/* Keyboard Shortcuts (placeholder) */}
      <section>
        <h2 className="notion-heading-3 mb-4 text-muted-foreground">
          {i18n.language === 'ar' ? 'اختصارات لوحة المفاتيح' : 'Keyboard Shortcuts'}
        </h2>
        <div className="rounded-lg border divide-y">
          {[
            { key: 'Ctrl + N', action: t('document.newPage') },
            { key: 'Ctrl + S', action: i18n.language === 'ar' ? 'حفظ' : 'Save' },
            { key: 'Ctrl + /', action: i18n.language === 'ar' ? 'قائمة الأوامر' : 'Command menu' },
            { key: 'Ctrl + \\', action: i18n.language === 'ar' ? 'إخفاء الشريط الجانبي' : 'Toggle sidebar' },
          ].map(({ key, action }) => (
            <div key={key} className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-muted-foreground">{action}</span>
              <kbd className="px-2 py-1 rounded bg-secondary text-xs font-mono">
                {key}
              </kbd>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
